import { FC, useId } from 'react'

import { ALERT_INTENT, Alert, Info } from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { useEmbroideryPrices } from '../../../../../../containers/EmbroideryContainer/providers/EmbroideryPricingProvider'
import { EmbroideryItemOfType } from '../../../../../../containers/EmbroideryContainer/providers/types'
import { useEmbroiderySiteMessages } from '../../../../../../containers/EmbroideryContainer/useEmbroiderySiteMessages'
import { StyledTooltip, ThreadEmojiWrapper } from '../../../primitives/shared-form-styles'
import { TooltipButton } from '../LogoColorOptions/styles'
import { LogoPicker, LogoPickerProps } from '../LogoPicker/LogoPicker'

import {
  EditorSection,
  FinalSaleNotice,
  SummaryHeader,
  SummaryLogoName,
  SummaryPrice,
} from './styles'

export type LogoSummaryStepProps = {
  /** The logo item as "Update garment" would save it, once a logo and placement are chosen. */
  item: EmbroideryItemOfType<'logo'> | undefined
  picker: Omit<LogoPickerProps, 'labelId'>
  /** Explains the missing "Add New Logo" tile when another garment already carries a new logo. */
  isNewLogoTaken: boolean
}

/**
 * The logo chosen for the garment, picked from the customer's logos. A fresh upload lands here
 * already chosen.
 */
export const LogoSummaryStep: FC<LogoSummaryStepProps> = ({ item, picker, isNewLogoTaken }) => {
  const { formatPrice, priceInCentsForItem } = useEmbroideryPrices()
  const { logoColorSelectionTooltip } = useEmbroiderySiteMessages()
  const {
    embroidery: {
      modal: {
        forms: { dtcLogoEmbroideryForm: copy, logoFields },
      },
    },
  } = useTranslation()
  const labelId = useId()

  const logoFile = item?.values.logoFile
  const colorLabel =
    logoFile?.digitizationSetting === 'BLACK_WHITE'
      ? copy.logoStrings.LOGO_BLACK_WHITE.title
      : copy.logoStrings.LOGO_COLOR.title
  const colorPriceInCents = item && priceInCentsForItem(item)

  return (
    <EditorSection data-logo-step=''>
      <div>
        <SummaryHeader as='div'>
          <span id={labelId}>{logoFields.summaryLabel}</span>
          {logoFile !== undefined && (
            <>
              <SummaryLogoName>{logoFile.name}</SummaryLogoName>
              <SummaryPrice>
                {colorLabel}
                {colorPriceInCents !== undefined && (
                  <>
                    ,<span>{formatPrice(colorPriceInCents, true)}</span>
                  </>
                )}
                {logoColorSelectionTooltip !== undefined && (
                  <StyledTooltip content={<>{logoColorSelectionTooltip}</>} placement='bottom-end'>
                    <TooltipButton type='button' aria-label={copy.ariaLabelLogoColorTooltip}>
                      <Info height='12px' width='12px' />
                    </TooltipButton>
                  </StyledTooltip>
                )}
              </SummaryPrice>
            </>
          )}
        </SummaryHeader>

        <LogoPicker labelId={labelId} {...picker} />
      </div>

      {isNewLogoTaken && (
        <Alert intent={ALERT_INTENT.Warn}>
          <Alert.Message>{copy.digitizationOneAtATime}</Alert.Message>
        </Alert>
      )}

      {item?.values.requiresSetup && (
        <FinalSaleNotice>
          <ThreadEmojiWrapper aria-hidden>🧵</ThreadEmojiWrapper>
          <span>{copy.digitizationDisclaimer}</span>
        </FinalSaleNotice>
      )}
    </EditorSection>
  )
}
